import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { CheckSquare, Plane, AlertTriangle, Home, FileText, Users } from "lucide-react";
import RampMyTasks from "./RampMyTasks";
import RampFlightBoard from "./RampFlightBoard";
import RampFlightTasks from "./RampFlightTasks";
import RampTaskUpdate from "./RampTaskUpdate";
import RampIncident from "./RampIncident";
import RampLogin from "./RampLogin";
import RampReports from "./RampReports";
import RampShiftBriefing from "./RampShiftBriefing";
import RampStaffDuties from "./RampStaffDuties";
import { useState } from "react";

const isSupervisor = role => role === "supervisor" || role === "dispatcher";

export default function RampApp() {
  const [rampUser, setRampUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ramp_user") || "null"); } catch { return null; }
  });

  if (!rampUser) {
    return <RampLogin onLogin={u => { sessionStorage.setItem("ramp_user", JSON.stringify(u)); setRampUser(u); }} />;
  }

  // For supervisors, replace "My Tasks" with "Shift Briefing"
  const homeTab  = isSupervisor(rampUser.role) ? "/ramp/briefing" : "/ramp/tasks";
  const HomeIcon = isSupervisor(rampUser.role) ? Users : CheckSquare;
  const homeLabel = isSupervisor(rampUser.role) ? "Briefing" : "My Tasks";

  const BOTTOM_NAV = [
    { to: homeTab,        icon: HomeIcon,      label: homeLabel  },
    { to: "/ramp/flights", icon: Plane,        label: "Flights"  },
    { to: "/ramp/incident",icon: AlertTriangle, label: "Report"  },
    { to: "/ramp/reports", icon: FileText,     label: "Reports"  },
  ];

  return (
    <div className="flex flex-col h-screen bg-[#07111f] text-white" style={{ fontFamily: "system-ui, sans-serif" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-[#0a1828] border-b border-orange-500/15 flex-shrink-0">
        <div>
          <div className="text-[10px] text-orange-400 font-bold uppercase tracking-widest">Ramp Control</div>
          <div className="text-[9px] text-slate-500">{rampUser.name} · {rampUser.station}</div>
        </div>
        <button
          onClick={() => { sessionStorage.removeItem("ramp_user"); setRampUser(null); }}
          className="flex items-center gap-1.5 text-[11px] font-bold text-white bg-orange-500/20 hover:bg-orange-500/40 border border-orange-500/40 px-3 py-2 rounded-xl transition-all active:scale-95"
          data-testid="complete-shift-btn"
        >
          Complete Shift
        </button>
      </header>

      {/* Page content */}
      <div className="flex-1 overflow-y-auto">
        <Routes>
          <Route index element={<Navigate to={homeTab.replace("/ramp/", "")} replace />} />
          {/* Supervisor-specific routes */}
          <Route path="briefing"                 element={<RampShiftBriefing user={rampUser} />} />
          <Route path="assign-duties/:staffId"   element={<RampStaffDuties user={rampUser} />} />
          {/* Staff routes */}
          <Route path="tasks"                    element={<RampMyTasks user={rampUser} />} />
          <Route path="flights"                  element={<RampFlightBoard user={rampUser} />} />
          <Route path="flight-tasks/:flightId"   element={<RampFlightTasks user={rampUser} />} />
          <Route path="task/:taskId"             element={<RampTaskUpdate user={rampUser} />} />
          <Route path="incident"                 element={<RampIncident user={rampUser} />} />
          <Route path="reports"                  element={<RampReports user={rampUser} />} />
        </Routes>
      </div>

      {/* Bottom Nav */}
      <nav className="flex-shrink-0 bg-[#0a1828] border-t border-orange-500/15 flex">
        {BOTTOM_NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-semibold transition-colors ${isActive ? "text-orange-400" : "text-slate-500"}`
            }
            data-testid={`ramp-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
        <NavLink to="/" className="flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-semibold text-slate-500">
          <Home size={20} />
          FS Home
        </NavLink>
      </nav>
    </div>
  );
}
